import { Injectable } from '@angular/core';
import { Store } from '@sama/app-state';
import { assert } from '@sama/js-core';
import { ShapeLocationState2D } from '../../../../cross-team-shared/annotation/shared/state/shape-location-state';
import { AbstractToolHelper } from '../../../../cross-team-shared/annotation/shared/tool-helpers/abstract-tool-helper';
import { UrlRequestService } from '../../../../cross-team-shared/misc-services/resources/url-request.service';
import { TokenManagementService } from '../../../../cross-team-shared/misc-services/token-management.service';
import { Point2D } from '../../../../team-apis/otterdam/answers/shape-typings';
import { AbstractSensorContentStore2D } from '../../state/abstract-sensor-content-store-2d';
import { getClickxOptionsOrThrow } from './clickx-tool-helper';

type InferenceServerResponse = [{ Polygon: Point2D[] }];

@Injectable()
export class PolygonInferenceService {
  constructor(
    private store: AbstractSensorContentStore2D,
    private tokenService: TokenManagementService,
    private urlRequestService: UrlRequestService,
  ) {}

  async infer(
    keyLocationStore: Store<ShapeLocationState2D>,
    helper: AbstractToolHelper,
    shapeIndex: number,
  ) {
    const token = await this.tokenService.getToken('ml_infer');
    if (!helper.isActive()) {
      return;
    }

    let response: InferenceServerResponse;
    try {
      response = await this.sendRequest(keyLocationStore, shapeIndex, token);
    } catch {
      helper.cancel({ source: 'model_request_failure' });
      return;
    }
    if (helper.isActive()) {
      keyLocationStore('points').set(response[0].Polygon);
    }
  }

  private async sendRequest(
    keyLocationStore: Store<ShapeLocationState2D>,
    shapeIndex: number,
    token: string,
  ) {
    const modelInputPoints = keyLocationStore.state().model_input_points;
    assert(modelInputPoints, 'Model input points are required');

    const state = this.store.state();
    const imageUrl =
      state.sensorRendererState.sensorFrames[
        state.frameNumber
      ]?.imageUrl.toString();
    assert(imageUrl, 'The frame must already be loaded');

    const modelInput = this.buildModelInput(
      imageUrl,
      modelInputPoints,
      shapeIndex,
    );
    const clickxOptions = getClickxOptionsOrThrow(this.store.state().output);
    return this.urlRequestService.request<InferenceServerResponse>(
      'POST',
      clickxOptions.model_id,
      { headers: buildRequestHeaders(token), body: [modelInput] },
    );
  }

  private buildModelInput(
    imageUrl: string,
    modelInputPoints: Point2D[],
    shapeIndex: number,
  ) {
    const { project, task, currentUser, output, stepId } = this.store.state();
    return {
      Image_url: imageUrl,
      Clicks: modelInputPoints,
      'Project ID': project.id,
      'Task ID': task.id,
      'Agent ID': currentUser.id,
      'Shape Index': shapeIndex,
      'Output ID': output.id,
      Round: task.round,
      'Step ID': stepId,
    };
  }
}

function buildRequestHeaders(token: string) {
  return {
    'Content-type': 'application/json; format=pandas-records',
    Authorization: 'Bearer ' + token,
  };
}
